import { useCallback, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Container, Header } from "components/Page"
import { Route, Switch, useLocation, useParams } from "react-router-dom"
import { activateCompany, deactivateCompany, fetchCompany } from "api/companies"
import { queryCache, useMutation, useQuery } from "react-query"
import { styledTabClasses, styledTabsProps } from "components/Tabs"

import AgenciesCompany from "./Agencies"
import { AuthorizedRoute } from "components/AuthorizedRoute"
import Badge from "components/Badge"
import Breadcrumbs from "components/Breadcrumbs"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { ReactComponent as CheckIcon } from "assets/check.svg"
import { CodeSublabel } from "components/HeaderSublabel/CodeSublabel"
import { ColorMap } from "utils/color"
import { ReactComponent as Deactivate } from "assets/icon-deactivate.svg"
import Documents from "./Documents"
import MoreActions from "components/MoreActions"
import Profile from "./Profile"
import { Surface } from "components/Surface"
import { TabLink } from "components/Link"
import Tabs from "@material-ui/core/Tabs"
import { css, cx } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

const styles = {
  btn: css`
    > span {
      text-transform: capitalize;
    }
  `,
  main: css`
    flex: 1;
    display: flex;
    flex-direction: column;
  `,
  tabContent: css`
    margin-top: var(--spacing-xs);
  `,
  surface: css`
    padding: var(--spacing-xxl-2) 0px 0px;
  `,
  wrapper: css`
    padding: var(--spacing-xl) var(--spacing-m) var(--spacing-m);
  `,
  documents: css`
    display: flex;
    grid-column-gap: var(--spacing-xxl-2);
  `,
}

const tabs = [
  "profile" /* t(company.tabs.profile) */,
  "agencies" /* t(company.tabs.agencies) */,
  "documents" /* t(company.tabs.documents) */,
]

function CompanyDetails() {
  const { t } = useI18n()
  const { id, tab = "profile" } = useParams<{ id: string; tab: string }>()
  const { pathname } = useLocation()
  const paths = pathname.split("/")
  const entity = paths[1]
  const { hasAuthority } = useAuthenticated()

  const { data, isLoading } = useQuery(["companies", id], fetchCompany)
  const [modalInfo, setModalInfo] = useState<ModalInfo>(undefined)

  const isModalOpen = Boolean(modalInfo)

  const [activate] = useMutation(activateCompany, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("companies")
    },
    onError: () => setModalInfo(undefined),
  })

  const [deactivate] = useMutation(deactivateCompany, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("companies")
    },
    onError: () => setModalInfo(undefined),
  })

  const onActivate = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("company.modal.activate.title"),
        description: t("company.modal.activate.description"),
        submitLabel: t("modal.label.activate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await activate(id),
      })
    },
    [activate, t],
  )

  const onDeactivate = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("company.modal.deactivate.title"),
        description: t("company.modal.deactivate.description"),
        submitLabel: t("modal.label.deactivate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await deactivate(id),
      })
    },
    [deactivate, t],
  )

  const isAdmin =
    hasAuthority(["AUTH_ACCESS_AGENCY_ADMIN"]) ||
    hasAuthority(["AUTH_ACCESS_MARKET_ADMIN"])
  const activeTabs =
    entity === "market-companies"
      ? tabs
      : tabs.filter((tb) => tb !== "agencies")

  const adminTabs = isAdmin
    ? activeTabs.filter((tb) => tb !== "documents")
    : activeTabs

  return (
    <Container>
      <>
        <Breadcrumbs
          current={data?.name}
          items={[
            {
              backLink: `${t("nav.admin")} / ${t("nav.admin.company")}`,
              to: `/${entity}`,
            },
          ]}
        />
        <Header
          label={data?.name}
          badge={
            <Badge color={ColorMap[data?.status as any]}>{data?.status}</Badge>
          }
          subLabel={<CodeSublabel code={data?.code} />}
        >
          <ButtonGroup>
            <MoreActions
              options={[
                // ** ACTIVATE
                {
                  label: t("activate"),
                  icon: <CheckIcon />,
                  hidden:
                    !(data?.status === "NEW" || data?.status === "INACTIVE") ||
                    !hasAuthority(["AUTH_EDIT_COMPANY"]),
                  onClick: () => onActivate(id),
                },
                // ** DEACTIVATE
                {
                  label: t("deactivate"),
                  icon: <Deactivate />,
                  hidden:
                    data?.status !== "ACTIVE" ||
                    !hasAuthority(["AUTH_EDIT_COMPANY"]),
                  onClick: () => onDeactivate(id),
                },
              ]}
            />
          </ButtonGroup>
        </Header>
        <Surface flexDirection="row" className={styles.wrapper}>
          <div className={styles.main}>
            <Tabs value={tab} {...styledTabsProps}>
              {adminTabs.map((tab, idx) => (
                <TabLink
                  key={idx}
                  to={`/${entity}/${id}/${tab}`}
                  classes={styledTabClasses}
                  value={tab}
                  label={t(`company.tabs.${tab}`)}
                />
              ))}
            </Tabs>
            <>
              <Switch>
                <AuthorizedRoute
                  allowed={["AUTH_VIEW_COMPANY"]}
                  path={`/${entity}/:id/profile`}
                >
                  <Profile data={data} loading={isLoading} />
                </AuthorizedRoute>
                <Route path={`/${entity}/:id/agencies`}>
                  <Surface className={styles.surface}>
                    <AgenciesCompany />
                  </Surface>
                </Route>
                <AuthorizedRoute
                  allowed={["AUTH_VIEW_COMPANY_DOCUMENT"]}
                  path={`/${entity}/:id/documents`}
                >
                  <Surface className={cx(styles.surface, styles.documents)}>
                    <Documents />
                  </Surface>
                </AuthorizedRoute>
              </Switch>
            </>
          </div>
        </Surface>

        <ConfirmationModal
          open={isModalOpen}
          onCancel={() => setModalInfo(undefined)}
          {...modalInfo}
        />
      </>
    </Container>
  )
}

export default CompanyDetails
